import { useSelector, useDispatch } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemText,
    Paper,
    useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    Close as CloseIcon,
    Warning as WarningIcon,
    AccountTree as TemplateIcon,
    Person as PersonIcon
} from '@mui/icons-material';

import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';

const GoalUsageDialog = () => {
    const dispatch = useDispatch();
    const theme = useTheme();

    // Redux state
    const isOpen = useSelector(selectors.isGoalUsageDialogOpenSelector);
    const usageData = useSelector(selectors.goalUsageDataSelector);

    const handleClose = () => {
        dispatch(actions.closeGoalUsageDialog());
    };

    if (!usageData) return null;

    const { message, usageDetails, suggestion } = usageData;
    const templates = (usageDetails?.templates && Array.isArray(usageDetails.templates)) ? usageDetails.templates : [];
    const userKpis = (usageDetails?.userKpis && Array.isArray(usageDetails.userKpis)) ? usageDetails.userKpis : [];
    const totalUsages = templates.length + userKpis.length;

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                <WarningIcon color="warning" />
                Cannot Delete Goal
            </DialogTitle>

            <DialogContent dividers>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {typeof message === 'string' ? message : 'Cannot delete goal - it is currently in use.'}
                </Alert>

                {suggestion && typeof suggestion === 'string' && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {suggestion}
                    </Typography>
                )}

                <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                            label={totalUsages} 
                            color="primary" 
                            size="small" 
                        />
                        Usage Details
                    </Typography>
                    
                    <Divider sx={{ mb: 2 }} />

                    {/* Templates Section */}
                    {templates.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography 
                                variant="subtitle1" 
                                gutterBottom 
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}
                            >
                                <TemplateIcon color="primary" fontSize="small" />
                                Templates ({templates.length})
                            </Typography>
                            <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                                <List dense>
                                    {templates.filter(template => template && typeof template === 'object').map((template, index) => (
                                        <ListItem 
                                            key={template.id}
                                            sx={{ 
                                                borderBottom: index < templates.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                                '&:hover': { backgroundColor: alpha(theme.palette.divider, 0.06) }
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {typeof template.name === 'string' ? template.name : 'Unknown Template'}
                                                        </Typography>
                                                        {template.category && typeof template.category === 'string' && (
                                                            <Chip 
                                                                label={template.category} 
                                                                size="small" 
                                                                variant="outlined"
                                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={
                                                    template.description && typeof template.description === 'string' ? (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {template.description}
                                                        </Typography>
                                                    ) : null
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        </Box>
                    )}

                    {/* User KPIs Section */}
                    {userKpis.length > 0 && (
                        <Box>
                            <Typography 
                                variant="subtitle1" 
                                gutterBottom 
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}
                            >
                                <PersonIcon color="secondary" fontSize="small" />
                                Assigned to Users ({userKpis.length})
                            </Typography>
                            <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                                <List dense>
                                    {userKpis.filter(userKpi => userKpi && typeof userKpi === 'object').map((userKpi, index) => (
                                        <ListItem 
                                            key={userKpi.id}
                                            sx={{ 
                                                borderBottom: index < userKpis.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                                '&:hover': { backgroundColor: alpha(theme.palette.divider, 0.06) }
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {typeof userKpi.userId === 'string' ? userKpi.userId : 'Unknown User'}
                                                        </Typography>
                                                        {userKpi.designation && typeof userKpi.designation === 'string' && (
                                                            <Chip 
                                                                label={userKpi.designation} 
                                                                size="small" 
                                                                color="secondary"
                                                                variant="outlined"
                                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                                            />
                                                        )}
                                                        {userKpi.periodName && typeof userKpi.periodName === 'string' && (
                                                            <Chip 
                                                                label={userKpi.periodName} 
                                                                size="small" 
                                                                variant="outlined"
                                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={
                                                    userKpi.category && typeof userKpi.category === 'string' ? (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Category: {userKpi.category}
                                                        </Typography>
                                                    ) : null
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button
                    onClick={handleClose}
                    startIcon={<CloseIcon />}
                    variant="contained"
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GoalUsageDialog;
